import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Branch, Warehouse } from '../utils/types';
import {
  createBranch,
  getAllBranches,
  updateBranch,
  getBranchById,
  setBranchActive,
  getBranchWarehouses,
  addWarehouseToBranch,
  removeWarehouseFromBranch,
  deleteBranch
} from '../services/branchService';
import { getAllWarehouses } from '../services/inventoryService';
import { useAuth } from '../contexts/AuthContext';

const BranchesPage: React.FC = () => {
  const { currentUser } = useAuth();

  // حالة الفروع
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // حالة النموذج
  const [openBranchDialog, setOpenBranchDialog] = useState(false);
  const [openWarehouseDialog, setOpenWarehouseDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [currentBranch, setCurrentBranch] = useState<Branch | null>(null);
  const [newBranch, setNewBranch] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    warehouseIds: [] as string[]
  });

  // حالة المخازن
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>([]);
  const [branchWarehouses, setBranchWarehouses] = useState<Warehouse[]>([]);

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // تحميل البيانات عند تحميل الصفحة
  useEffect(() => {
    loadBranches();
    loadWarehouses();
  }, []);

  // تحميل الفروع من قاعدة البيانات
  const loadBranches = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedBranches = await getAllBranches(true); // إحضار جميع الفروع بما فيها غير النشطة
      setBranches(fetchedBranches);
    } catch (err) {
      console.error('Error loading branches:', err);
      setError('حدث خطأ أثناء تحميل بيانات الفروع');
    } finally {
      setLoading(false);
    }
  };

  // تحميل المخازن من قاعدة البيانات
  const loadWarehouses = async () => {
    try {
      const fetchedWarehouses = await getAllWarehouses();
      setWarehouses(fetchedWarehouses);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  };

  // تحميل مخازن فرع محدد
  const loadBranchWarehouses = async (branchId: string) => {
    try {
      if (!branchId) return;
      const fetchedWarehouses = await getBranchWarehouses(branchId);
      setBranchWarehouses(fetchedWarehouses);
    } catch (err) {
      console.error('Error loading branch warehouses:', err);
    }
  };

  // فتح نموذج إضافة فرع جديد
  const handleAddBranch = () => {
    setCurrentBranch(null);
    setNewBranch({
      name: '',
      address: '',
      phone: '',
      email: '',
      warehouseIds: []
    });
    setSelectedWarehouseIds([]);
    setOpenBranchDialog(true);
  };

  // فتح نموذج تعديل فرع
  const handleEditBranch = async (branch: Branch) => {
    setCurrentBranch(branch);
    setNewBranch({
      name: branch.name,
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      warehouseIds: branch.warehouseIds || []
    });
    setSelectedWarehouseIds(branch.warehouseIds || []);
    setOpenBranchDialog(true);

    // تحميل مخازن الفرع
    await loadBranchWarehouses(branch.id);
  };

  // فتح نموذج إدارة مخازن الفرع
  const handleManageWarehouses = async (branch: Branch) => {
    setCurrentBranch(branch);
    setSelectedWarehouseIds(branch.warehouseIds || []);
    await loadBranchWarehouses(branch.id);
    setOpenWarehouseDialog(true);
  };

  // تغيير حالة نشاط الفرع
  const handleToggleActive = async (branch: Branch) => {
    try {
      await setBranchActive(branch.id, !branch.isActive);
      await loadBranches();

      setSnackbar({
        open: true,
        message: `تم ${branch.isActive ? 'تعطيل' : 'تفعيل'} الفرع بنجاح`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error toggling branch status:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تغيير حالة الفرع',
        severity: 'error'
      });
    }
  };

  // حفظ الفرع (إضافة أو تعديل)
  const handleSaveBranch = async () => {
    try {
      console.log('handleSaveBranch: Starting to save branch');
      console.log('newBranch:', newBranch);
      console.log('currentBranch:', currentBranch);
      console.log('selectedWarehouseIds:', selectedWarehouseIds);

      if (!newBranch.name) {
        console.log('Branch name is required');
        setSnackbar({
          open: true,
          message: 'يرجى إدخال اسم الفرع',
          severity: 'error'
        });
        return;
      }

      if (currentBranch) {
        // تعديل فرع موجود
        console.log(`Updating existing branch with ID: ${currentBranch.id}`);

        const updateData = {
          name: newBranch.name,
          address: newBranch.address,
          phone: newBranch.phone,
          email: newBranch.email,
          warehouseIds: selectedWarehouseIds
        };

        console.log('Update data:', updateData);
        await updateBranch(currentBranch.id, updateData);
        console.log('Branch updated successfully');

        setSnackbar({
          open: true,
          message: 'تم تحديث الفرع بنجاح',
          severity: 'success'
        });
      } else {
        // إضافة فرع جديد
        console.log('Creating new branch');

        const branchData: Partial<Branch> = {
          name: newBranch.name,
          address: newBranch.address,
          phone: newBranch.phone,
          email: newBranch.email,
          warehouseIds: selectedWarehouseIds,
          isActive: true,
        };

        console.log('New branch data:', branchData);
        await createBranch(branchData as Branch);
        console.log('Branch created successfully');

        setSnackbar({
          open: true,
          message: 'تم إضافة الفرع بنجاح',
          severity: 'success'
        });
      }

      setOpenBranchDialog(false);
      console.log('Loading branches after save...');
      await loadBranches();
      console.log('Branches loaded successfully');
    } catch (err: any) {
      console.error('Error saving branch:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء حفظ الفرع',
        severity: 'error'
      });
    }
  };

  // إضافة مخزن إلى الفرع
  const handleAddWarehouseToBranch = async (warehouseId: string) => {
    if (!currentBranch || !warehouseId) return;

    try {
      await addWarehouseToBranch(currentBranch.id, warehouseId);

      // تحديث القائمة المحلية
      setSelectedWarehouseIds([...selectedWarehouseIds, warehouseId]);
      await loadBranchWarehouses(currentBranch.id);
      await loadBranches();

      setSnackbar({
        open: true,
        message: 'تم إضافة المخزن للفرع بنجاح',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error adding warehouse to branch:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة المخزن',
        severity: 'error'
      });
    }
  };

  // إزالة مخزن من الفرع
  const handleRemoveWarehouseFromBranch = async (warehouseId: string) => {
    if (!currentBranch || !warehouseId) return;

    try {
      await removeWarehouseFromBranch(currentBranch.id, warehouseId);

      // تحديث القائمة المحلية
      setSelectedWarehouseIds(selectedWarehouseIds.filter(id => id !== warehouseId));
      await loadBranchWarehouses(currentBranch.id);
      await loadBranches();

      setSnackbar({
        open: true,
        message: 'تم إزالة المخزن من الفرع بنجاح',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error removing warehouse from branch:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إزالة المخزن',
        severity: 'error'
      });
    }
  };

  // فتح حوار تأكيد الحذف
  const handleDeleteConfirm = (branch: Branch) => {
    setCurrentBranch(branch);
    setForceDelete(false);
    setOpenDeleteDialog(true);
  };

  // حذف الفرع
  const handleDeleteBranch = async () => {
    if (!currentBranch) return;

    try {
      console.log(`Attempting to delete branch: ${currentBranch.id}, force: ${forceDelete}`);

      await deleteBranch(currentBranch.id, forceDelete);
      console.log('Branch deleted successfully');

      setSnackbar({
        open: true,
        message: 'تم حذف الفرع بنجاح',
        severity: 'success'
      });

      setOpenDeleteDialog(false);
      await loadBranches();
    } catch (err: any) {
      console.error('Error deleting branch:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء حذف الفرع',
        severity: 'error'
      });
    }
  };

  // إغلاق الإشعار
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إدارة الفروع
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddBranch}
        >
          إضافة فرع جديد
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>اسم الفرع</TableCell>
              <TableCell>العنوان</TableCell>
              <TableCell>رقم الهاتف</TableCell>
              <TableCell>البريد الإلكتروني</TableCell>
              <TableCell>المخازن</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {branches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  لا توجد فروع مضافة حتى الآن
                </TableCell>
              </TableRow>
            ) : (
              branches.map((branch) => (
                <TableRow key={branch.id}>
                  <TableCell>{branch.name}</TableCell>
                  <TableCell>{branch.address || '-'}</TableCell>
                  <TableCell>{branch.phone || '-'}</TableCell>
                  <TableCell>{branch.email || '-'}</TableCell>
                  <TableCell>
                    {branch.warehouseIds && branch.warehouseIds.length > 0 ? (
                      <Chip
                        label={`${branch.warehouseIds.length} مخزن`}
                        color="primary"
                        variant="outlined"
                        onClick={() => handleManageWarehouses(branch)}
                      />
                    ) : (
                      <Chip
                        label="لا يوجد"
                        color="warning"
                        variant="outlined"
                        onClick={() => handleManageWarehouses(branch)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={branch.isActive ? 'نشط' : 'غير نشط'}
                      color={branch.isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleEditBranch(branch)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color={branch.isActive ? 'error' : 'success'}
                      onClick={() => handleToggleActive(branch)}
                    >
                      {branch.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteConfirm(branch)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* نموذج إضافة/تعديل فرع */}
      <Dialog open={openBranchDialog} onClose={() => setOpenBranchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentBranch ? `تعديل فرع: ${currentBranch.name}` : 'إضافة فرع جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="اسم الفرع"
                fullWidth
                required
                value={newBranch.name}
                onChange={(e) => setNewBranch({ ...newBranch, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="العنوان"
                fullWidth
                value={newBranch.address}
                onChange={(e) => setNewBranch({ ...newBranch, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="رقم الهاتف"
                fullWidth
                value={newBranch.phone}
                onChange={(e) => setNewBranch({ ...newBranch, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="البريد الإلكتروني"
                fullWidth
                type="email"
                value={newBranch.email}
                onChange={(e) => setNewBranch({ ...newBranch, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>المخازن المرتبطة</InputLabel>
                <Select
                  multiple
                  value={selectedWarehouseIds}
                  onChange={(e) => setSelectedWarehouseIds(e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const warehouse = warehouses.find(w => w.id === value);
                        return (
                          <Chip key={value} label={warehouse ? warehouse.name : value} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBranchDialog(false)} color="inherit">إلغاء</Button>
          <Button onClick={handleSaveBranch} color="primary" variant="contained">حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* نموذج إدارة مخازن الفرع */}
      <Dialog open={openWarehouseDialog} onClose={() => setOpenWarehouseDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentBranch ? `إدارة مخازن فرع: ${currentBranch.name}` : 'إدارة المخازن'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                المخازن المرتبطة بالفرع
              </Typography>
              {branchWarehouses.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>اسم المخزن</TableCell>
                        <TableCell>الموقع</TableCell>
                        <TableCell>الإجراءات</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branchWarehouses.map((warehouse) => (
                        <TableRow key={warehouse.id}>
                          <TableCell>{warehouse.name}</TableCell>
                          <TableCell>{warehouse.location || '-'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleRemoveWarehouseFromBranch(warehouse.id)}
                            >
                              إزالة
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  لا توجد مخازن مرتبطة بهذا الفرع حالياً
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                إضافة مخزن للفرع
              </Typography>
              <FormControl fullWidth>
                <InputLabel>اختر مخزناً</InputLabel>
                <Select
                  value=""
                  onChange={(e) => handleAddWarehouseToBranch(e.target.value as string)}
                  label="اختر مخزناً"
                >
                  {warehouses
                    .filter(w => !selectedWarehouseIds.includes(w.id))
                    .map((warehouse) => (
                      <MenuItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWarehouseDialog(false)} color="primary">إغلاق</Button>
        </DialogActions>
      </Dialog>

      {/* حوار تأكيد الحذف */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>حذف الفرع</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography>
              هل أنت متأكد من حذف الفرع "{currentBranch?.name}"?
            </Typography>
            {currentBranch?.warehouseIds && currentBranch.warehouseIds.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                هذا الفرع يحتوي على {currentBranch.warehouseIds.length} مخزن. الحذف القسري سيؤدي إلى إزالة ارتباط المخازن بالفرع.
              </Alert>
            )}
            <FormControl sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>حذف قسري:</Typography>
              <Switch
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
                color="warning"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (استخدم هذا الخيار لحذف الفرع حتى لو كان مرتبطاً بمخازن أو موظفين)
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">إلغاء</Button>
          <Button onClick={handleDeleteBranch} color="error" variant="contained">حذف</Button>
        </DialogActions>
      </Dialog>

      {/* إشعارات النظام */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BranchesPage;